import type { GameInstanceConfig, GamePlugin, RoundContext } from '../types';
import {
  compareTeamsHolesWon,
  teamBestNet,
  teamStablefordTotal,
  teamWorstNet,
  vegasHoleDiff,
} from '@/lib/games/formats/team-formats';
import {
  applyMoneyToNetOwed,
  getMoneyAmount,
  getStakeConfig,
  pointsFromTotals,
} from '../stakes-helpers';

function distributeTeamWinnings(
  teams: string[][],
  winTeam: number,
  amountCents: number,
): Record<string, number> {
  const netOwed: Record<string, number> = {};
  teams.forEach((t) => t.forEach((k) => { netOwed[k] = 0; }));
  const lose = winTeam === 0 ? 1 : 0;
  const winAmt = amountCents / teams[winTeam].length;
  const loseAmt = amountCents / teams[lose].length;
  teams[winTeam].forEach((k) => { netOwed[k] = winAmt; });
  teams[lose].forEach((k) => { netOwed[k] = -loseAmt; });
  return netOwed;
}

export const fourBallPlugin: GamePlugin = {
  id: 'four-ball',
  label: 'Four Ball',
  description: '2v2 partner match play',
  requiresTeams: true,
  isMatch: true,
  minPlayers: 4,
  defaultStakes: { scoringType: 'money', money: 10 },
  compute(ctx: RoundContext, _config: GameInstanceConfig) {
    const stake = getStakeConfig(ctx.stakes, 'four-ball');
    const teams = ctx.teams;
    if (!teams || teams.length < 2) {
      return { formatId: 'four-ball', label: 'Four Ball', summary: 'Set up teams', netOwed: {} };
    }
    const diff = compareTeamsHolesWon(teams, ctx.players, ctx.holes, ctx.scores, (a, b) => a < b);
    const bet = getMoneyAmount(stake) * 100;
    const netOwed = diff === 0 ? {} : distributeTeamWinnings(teams, diff > 0 ? 0 : 1, Math.abs(diff) * bet);
    return {
      formatId: 'four-ball',
      label: 'Four Ball',
      summary: diff === 0 ? 'All square' : `Team ${diff > 0 ? 'A' : 'B'} ${Math.abs(diff)} up`,
      netOwed: applyMoneyToNetOwed(netOwed, stake),
      detail: { diff },
    };
  },
};

export const teamMatchPlayPlugin: GamePlugin = {
  id: 'team-match-play',
  label: 'Team Match Play',
  description: 'Team vs team holes won',
  requiresTeams: true,
  isMatch: true,
  minPlayers: 4,
  defaultStakes: { scoringType: 'money', money: 10 },
  compute(ctx: RoundContext, _config: GameInstanceConfig) {
    const stake = getStakeConfig(ctx.stakes, 'team-match-play');
    const teams = ctx.teams;
    if (!teams || teams.length < 2) {
      return { formatId: 'team-match-play', label: 'Team Match Play', summary: 'Set up teams', netOwed: {} };
    }
    const diff = compareTeamsHolesWon(teams, ctx.players, ctx.holes, ctx.scores, (a, b) => a < b);
    const bet = getMoneyAmount(stake) * 100;
    const netOwed = diff === 0 ? {} : distributeTeamWinnings(teams, diff > 0 ? 0 : 1, bet);
    return {
      formatId: 'team-match-play',
      label: 'Team Match Play',
      summary: diff === 0 ? 'All square' : `Team ${diff > 0 ? 'A' : 'B'} ${Math.abs(diff)} up`,
      netOwed: applyMoneyToNetOwed(netOwed, stake),
    };
  },
};

export const aggregatePlugin: GamePlugin = {
  id: 'aggregate',
  label: 'Aggregate',
  description: 'Sum both net scores per hole',
  requiresTeams: true,
  minPlayers: 4,
  defaultStakes: { scoringType: 'money', money: 10 },
  compute(ctx: RoundContext, _config: GameInstanceConfig) {
    const stake = getStakeConfig(ctx.stakes, 'aggregate');
    const teams = ctx.teams;
    if (!teams || teams.length < 2) {
      return { formatId: 'aggregate', label: 'Aggregate', summary: 'Set up teams', netOwed: {} };
    }
    let diff = 0;
    ctx.holes.forEach((_, hi) => {
      const sumA = teams[0].reduce((s, k) => {
        const p = ctx.players.find((x) => x.playerKey === k);
        if (!p) return s;
        const n = teamBestNet([k], hi, ctx.players, ctx.holes, ctx.scores, 'aggregate');
        return s + (n ?? 99);
      }, 0);
      const sumB = teams[1].reduce((s, k) => {
        const n = teamBestNet([k], hi, ctx.players, ctx.holes, ctx.scores, 'aggregate');
        return s + (n ?? 99);
      }, 0);
      if (sumA < sumB) diff++;
      else if (sumB < sumA) diff--;
    });
    const bet = getMoneyAmount(stake) * 100;
    const netOwed = diff === 0 ? {} : distributeTeamWinnings(teams, diff > 0 ? 0 : 1, Math.abs(diff) * bet);
    return {
      formatId: 'aggregate',
      label: 'Aggregate',
      summary: diff === 0 ? 'All square' : `Team ${diff > 0 ? 'A' : 'B'} leads`,
      netOwed: applyMoneyToNetOwed(netOwed, stake),
    };
  },
};

export const highLowPlugin: GamePlugin = {
  id: 'high-low',
  label: 'High-Low',
  description: 'Best + worst net combined',
  requiresTeams: true,
  minPlayers: 4,
  defaultStakes: { scoringType: 'money', money: 10 },
  compute(ctx: RoundContext, _config: GameInstanceConfig) {
    const stake = getStakeConfig(ctx.stakes, 'high-low');
    const teams = ctx.teams;
    if (!teams || teams.length < 2) {
      return { formatId: 'high-low', label: 'High-Low', summary: 'Set up teams', netOwed: {} };
    }
    let diff = 0;
    ctx.holes.forEach((_, hi) => {
      const lowA = teamBestNet(teams[0], hi, ctx.players, ctx.holes, ctx.scores, 'high-low') ?? 99;
      const highA = teamWorstNet(teams[0], hi, ctx.players, ctx.holes, ctx.scores, 'high-low') ?? 99;
      const lowB = teamBestNet(teams[1], hi, ctx.players, ctx.holes, ctx.scores, 'high-low') ?? 99;
      const highB = teamWorstNet(teams[1], hi, ctx.players, ctx.holes, ctx.scores, 'high-low') ?? 99;
      const combA = lowA + highA;
      const combB = lowB + highB;
      if (combA < combB) diff++;
      else if (combB < combA) diff--;
    });
    const bet = getMoneyAmount(stake) * 100;
    const netOwed = diff === 0 ? {} : distributeTeamWinnings(teams, diff > 0 ? 0 : 1, Math.abs(diff) * bet);
    return {
      formatId: 'high-low',
      label: 'High-Low',
      summary: diff === 0 ? 'All square' : `Team ${diff > 0 ? 'A' : 'B'} leads`,
      netOwed: applyMoneyToNetOwed(netOwed, stake),
    };
  },
};

export const teamStablefordPlugin: GamePlugin = {
  id: 'team-stableford',
  label: 'Team Stableford',
  description: 'Team best net → Stableford points',
  requiresTeams: true,
  minPlayers: 4,
  defaultStakes: { scoringType: 'money', money: 10 },
  compute(ctx: RoundContext, _config: GameInstanceConfig) {
    const stake = getStakeConfig(ctx.stakes, 'team-stableford');
    const teams = ctx.teams;
    if (!teams || teams.length < 2) {
      return { formatId: 'team-stableford', label: 'Team Stableford', summary: 'Set up teams', netOwed: {} };
    }
    const ptsA = teamStablefordTotal(teams[0], ctx.players, ctx.holes, ctx.scores);
    const ptsB = teamStablefordTotal(teams[1], ctx.players, ctx.holes, ctx.scores);
    const diff = ptsA - ptsB;
    const bet = getMoneyAmount(stake) * 100;
    const netOwed = diff === 0 ? {} : distributeTeamWinnings(teams, diff > 0 ? 0 : 1, bet);
    const totals: Record<string, number> = {};
    teams[0].forEach((k) => { totals[k] = ptsA; });
    teams[1].forEach((k) => { totals[k] = ptsB; });
    return {
      formatId: 'team-stableford',
      label: 'Team Stableford',
      summary: `Team A ${ptsA} – ${ptsB} Team B`,
      netOwed: applyMoneyToNetOwed(netOwed, stake),
      pointsAwarded: pointsFromTotals([...teams[0], ...teams[1]], totals, stake),
    };
  },
};
